import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Package, MapPin, User, Calendar, CheckCircle2, Truck, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  picked_up: 'bg-sky-100 text-sky-800 border-sky-200',
  in_transit: 'bg-blue-100 text-blue-800 border-blue-200',
  arrived_at_hub: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  awaiting_pickup: 'bg-[#F5EFE1] text-[#8F4426] border-[#B85C38]/40',
  out_for_delivery: 'bg-purple-100 text-purple-800 border-purple-200',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  returned: 'bg-rose-100 text-rose-800 border-rose-200',
  cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
};
const fmtDT = (s) => (s ? new Date(s).toLocaleString('fr-FR') : '—');

const PublicTracking = () => {
  const { tracking_number } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/shipments/track/${tracking_number}`;
    axios.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'Envoi introuvable'))
      .finally(() => setLoading(false));
  }, [tracking_number]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FDF6E3] via-white to-[#FDF6E3]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FDF6E3] via-white to-[#FDF6E3] p-4" data-testid="public-tracking-error">
        <Card className="max-w-md w-full border-2 border-rose-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-rose-500 mb-3" />
            <h1 className="text-xl font-bold text-rose-700">Envoi introuvable</h1>
            <p className="text-gray-500 text-sm mt-2">{error || `Aucun envoi ne correspond au numéro ${tracking_number}.`}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF6E3] via-white to-[#FDF6E3] py-8 px-4" data-testid="public-tracking-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-3">
            <Package className="w-8 h-8 text-amber-700" />
          </div>
          <h1 className="text-2xl font-bold text-[#1B3A4B]">Suivi de votre envoi</h1>
          <p className="text-xs font-mono text-amber-700 mt-1">{data.tracking_number}</p>
        </div>

        {/* Current status */}
        <Card className="border-2 border-amber-200">
          <CardContent className="p-6 text-center">
            <Badge className={`border text-base px-4 py-1.5 ${STATUS_COLORS[data.status]}`}>
              {data.status_label}
            </Badge>
            {data.is_late && (
              <Badge className="ml-2 text-base px-4 py-1.5 bg-red-600 text-white border-red-700" data-testid="tracking-late-badge">
                En retard
              </Badge>
            )}
            {data.delivered_at && (
              <p className="text-sm text-emerald-700 mt-3 flex items-center justify-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Livré le {fmtDT(data.delivered_at)}
              </p>
            )}
            {(data.estimated_departure_date || data.estimated_arrival_date) && (
              <div className="mt-4 pt-3 border-t border-amber-100 grid grid-cols-2 gap-3 text-xs">
                {data.estimated_departure_date && (
                  <div className="text-center">
                    <div className="uppercase font-bold text-gray-500 text-[10px] flex items-center justify-center gap-1">
                      <Truck className="w-3 h-3 text-[#B85C38]" /> Départ prévu
                    </div>
                    <div className="font-semibold text-[#2B3A2C] mt-0.5">
                      {new Date(data.estimated_departure_date).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                )}
                {data.estimated_arrival_date && (
                  <div className="text-center">
                    <div className="uppercase font-bold text-gray-500 text-[10px] flex items-center justify-center gap-1">
                      <Package className="w-3 h-3 text-[#B85C38]" /> Arrivée prévue
                    </div>
                    <div className={`font-semibold mt-0.5 ${data.is_late ? 'text-red-600' : 'text-[#2B3A2C]'}`}>
                      {new Date(data.estimated_arrival_date).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Route */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <div className="text-[10px] uppercase font-bold text-gray-500">Départ</div>
                <div className="font-semibold text-[#1B3A4B] flex items-center justify-center gap-1 mt-1">
                  <MapPin className="w-3.5 h-3.5 text-amber-600" /> {data.origin_city}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{data.sender_name}</div>
              </div>
              <div className="px-3">
                <Truck className="w-6 h-6 text-amber-600" />
              </div>
              <div className="text-center flex-1">
                <div className="text-[10px] uppercase font-bold text-gray-500">Arrivée</div>
                <div className="font-semibold text-[#1B3A4B] flex items-center justify-center gap-1 mt-1">
                  <MapPin className="w-3.5 h-3.5 text-amber-600" /> {data.destination_city}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{data.recipient_name}</div>
              </div>
            </div>
            {data.description && (
              <div className="mt-3 pt-3 border-t text-sm text-gray-600 text-center">
                <User className="w-3.5 h-3.5 inline mr-1" /> {data.description}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        {data.history?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-bold text-[#1B3A4B] mb-3 flex items-center gap-1">
                <Calendar className="w-4 h-4" /> Historique
              </h3>
              <div className="relative pl-6 space-y-3">
                {data.history.slice().reverse().map((h, i) => (
                  <div key={i} className="relative">
                    <div className="absolute -left-5 top-1.5 w-2.5 h-2.5 rounded-full bg-amber-500 ring-4 ring-amber-100" />
                    {i < data.history.length - 1 && (
                      <div className="absolute -left-4 top-4 bottom-0 w-0.5 bg-amber-200" style={{ height: '2rem' }} />
                    )}
                    <div className="text-sm font-medium text-[#1B3A4B]">{h.label}</div>
                    <div className="text-[11px] text-gray-500">{fmtDT(h.at)}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-gray-400">
          Suivi propulsé par GAB&apos;S SERVICES HABITAT
        </p>
      </div>
    </div>
  );
};

export default PublicTracking;
